import AccommodationIndividualSelectRoomTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomTile'
import AccommodationIndividualSelectRoomTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import React, { useCallback, useMemo } from 'react'
import BedbankIndividualRoomSelectRoomOption from './BedbankIndividualRoomSelectRoomOption'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { rem } from 'polished'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import AccommodationIndividualSelectRoomOptions from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptions'

interface Props {
  packageView: App.BedbankOfferPackageView
  optionVariant: 'stacked' | 'carousel'
  currentRoom: OfferPageRoom
  onOptionSelect: (packageOption: App.BedbankPackage, rate?: App.BedbankRate) => void
  remainingAvailableRoomsBySupplierCode: Record<string, number>
  remainingOverallAvailability: number
  checkInDate?: string
  checkOutDate?: string
}

function BedbankIndividualRoomSelectRoom({
  packageView,
  optionVariant,
  currentRoom,
  onOptionSelect,
  remainingAvailableRoomsBySupplierCode,
  remainingOverallAvailability,
  checkInDate,
  checkOutDate,
}: Props) {
  const hasPromotions = !!packageView.promotions.length
  const hasMobileOnlyPrice = useMemo(() => {
    return packageView.effectiveRates.some(rate => rate.mobilePromotion)
  }, [packageView])

  const currentlySelectedRate = useMemo(() => (
    currentRoom.rateId ? packageView.effectiveRates.find(rate => rate.id === currentRoom.rateId) : undefined
  ), [currentRoom.rateId, packageView.effectiveRates])

  const hasAvailability = (rate: App.BedbankRate) => {
    if (currentRoom.roomId === packageView.package.roomId) {
      if (currentlySelectedRate?.supplierCode === rate.supplierCode) {
        return true
      } else {
        return remainingAvailableRoomsBySupplierCode[rate.supplierCode] > 0
      }
    } else {
      return remainingOverallAvailability > 0
    }
  }
  const hasDates = !!(checkInDate && checkOutDate)

  const onSelectRoomOnly = useCallback(() => {
    onOptionSelect(packageView.package, undefined)
  }, [onOptionSelect, packageView.package])

  const carouselWidth = packageView.effectiveRates.length > 1 ? rem(300) : '100%'

  return <AccommodationIndividualSelectRoomTile roomPackage={packageView.package} isCurrentSelection={packageView.package.roomId === currentRoom.roomId}>
    <AccommodationRoomTileImage
      roomPackage={packageView.package}
      showBookmark={false}
    />
    <AccommodationIndividualSelectRoomTileDetails
      roomPackage={packageView.package}
      urgencyLabels={<>
        {hasPromotions && <BookingsCountLabel
          bookings={packageView.package.stats.booked}
          threshold="room"
        />}
        {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
      </>}
    />
    <AccommodationIndividualSelectRoomOptions hasOptions={hasDates}>
      {hasDates && optionVariant === 'carousel' &&
        <CardCarousel width={carouselWidth} gutterSize={20} gap={12} snap="start">
          {packageView.effectiveRates.map((rate, index) => <BedbankIndividualRoomSelectRoomOption
            key={rate.id}
            rate={rate}
            packageNumber={index + 1}
            packageView={packageView}
            currentRoom={currentRoom}
            onOptionSelect={onOptionSelect}
            hasAvailability={hasAvailability(rate)}
            variant="carousel"
          />)}
        </CardCarousel>}
      {hasDates && optionVariant === 'stacked' && packageView.effectiveRates.map((rate, index) => <BedbankIndividualRoomSelectRoomOption
        key={rate.id}
        rate={rate}
        packageNumber={index + 1}
        packageView={packageView}
        currentRoom={currentRoom}
        onOptionSelect={onOptionSelect}
        hasAvailability={hasAvailability(rate)}
        variant="stacked"
      />)}
      {!hasDates && <TextButton kind="secondary" onClick={onSelectRoomOnly}>Select</TextButton>}
    </AccommodationIndividualSelectRoomOptions>
  </AccommodationIndividualSelectRoomTile>
}

export default withOfferPageState((state) => ({
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
}), BedbankIndividualRoomSelectRoom)
